import type { ListMemoryThreadsResponse } from '@mastra/client-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import mastraClient from '../lib/mastraClient';

const THREADS_QUERY_KEY = ['AI', 'THREADS'];

/**
 * Hook to delete a thread on the server and drop it from the sidebar cache.
 * Optimistically removes the thread, rolls back on failure, and revalidates on settle.
 * @param agentName - The agent ID (required by the server for the delete)
 * @param resourceId - The resource ID or user ID (part of the threads cache key)
 * @returns Mutation to delete a thread by id
 */
export const useRemoveThread = ({ agentName, resourceId }: { agentName: string; resourceId: string }) => {
  const queryClient = useQueryClient();
  const queryKey = [...THREADS_QUERY_KEY, agentName, resourceId];

  return useMutation({
    mutationFn: async ({ threadId }: { threadId: string }) => {
      const client = await mastraClient();
      return client.deleteThread(threadId, { agentId: agentName });
    },
    onMutate: async ({ threadId }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousThreads = queryClient.getQueryData<ListMemoryThreadsResponse>(queryKey);

      queryClient.setQueryData<ListMemoryThreadsResponse>(queryKey, (old) => {
        if (!old?.threads) return old;
        return { ...old, threads: old.threads.filter((thread) => thread.id !== threadId) };
      });

      return { previousThreads };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousThreads) {
        queryClient.setQueryData(queryKey, context.previousThreads);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
